import { Clock, CheckCircle2, AlertTriangle, Hourglass, Calendar, ShieldCheck } from 'lucide-react';
import { formatHorasDecimales, horasDeAudios, horasDeAudiosAprobados } from '@/utils/formatDuration';

// Consolidado a propósito: el admin nunca debe ver ni escuchar el concepto de "preaprobación" —
// es un mecanismo interno (autoAprobar) para que la aprobación no dependa de resolver las
// asesorías en orden estricto. De cara al admin, una asesoría preaprobada cuenta y se muestra
// exactamente igual que una aprobada — no es una categoría propia.
export interface ResumenHorasEmpresa {
  horasTotales: number;
  horasAprobadas: number;
  horasRechazadas: number;
  // De las horasRechazadas: cuánto corresponde a audios ya aprobados individualmente dentro de
  // una asesoría que en general sigue REQUIERE_CORRECCIONES por otro motivo (ej. una evidencia
  // distinta rechazada). No resta de horasRechazadas — es un desglose informativo aparte.
  horasAprobadasEnRechazadas: number;
  horasPendientes: number;
  horasRestantes: number;
  pctAprobadas: number;
  pctRechazadas: number;
  pctPendientes: number;
  metaHoras: number;
}

interface HorasResumenCardProps {
  resumen: ResumenHorasEmpresa;
}

export function HorasResumenCard({ resumen }: HorasResumenCardProps) {
  const {
    horasTotales,
    horasAprobadas,
    horasRechazadas,
    horasAprobadasEnRechazadas,
    horasPendientes,
    horasRestantes,
    pctAprobadas,
    pctRechazadas,
    pctPendientes,
    metaHoras,
  } = resumen;

  // Segmented progress bar
  const pctAprobadasBar = Math.min(100, (horasAprobadas / metaHoras) * 100);
  const pctRechazadasBar = Math.min(100 - pctAprobadasBar, (horasRechazadas / metaHoras) * 100);
  const pctPendientesBar = Math.min(100 - pctAprobadasBar - pctRechazadasBar, (horasPendientes / metaHoras) * 100);

  return (
    <div className="admin-card p-5 mb-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-4 pb-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-700" />
          <h3 className="text-[15px] font-bold text-gray-900">Control y Resumen de Horas de Asesoría</h3>
        </div>
        <div className="flex items-center gap-3 text-[12px]">
          <span className="text-gray-500 font-medium">Meta de avance:</span>
          <span className="bg-gray-100 text-gray-800 font-bold px-2.5 py-0.5 rounded border border-gray-200">{metaHoras} Horas</span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-5">
        <div className="flex items-center justify-between text-[11px] text-gray-600 font-semibold mb-1.5">
          <span>Progreso Acumulado ({((horasAprobadas / metaHoras) * 100).toFixed(1)}%)</span>
          <span>{formatHorasDecimales(horasTotales)} Registradas en Total</span>
        </div>
        <div className="h-3.5 bg-gray-100 rounded-full overflow-hidden flex w-full p-0.5 border border-gray-200">
          <div
            style={{ width: `${pctAprobadasBar}%` }}
            className="bg-emerald-600 h-full rounded-l-full transition-all duration-500"
            title={`Aprobadas: ${formatHorasDecimales(horasAprobadas)} (${pctAprobadas}%)`}
          />
          <div
            style={{ width: `${pctRechazadasBar}%` }}
            className="bg-rose-500 h-full transition-all duration-500"
            title={`Rechazadas: ${formatHorasDecimales(horasRechazadas)} (${pctRechazadas}%)`}
          />
          <div
            style={{ width: `${pctPendientesBar}%` }}
            className="bg-blue-500 h-full rounded-r-full transition-all duration-500"
            title={`Pendientes: ${formatHorasDecimales(horasPendientes)} (${pctPendientes}%)`}
          />
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {/* Aprobadas */}
        <div className="bg-emerald-50/70 border border-emerald-200/80 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-emerald-800 text-[11px] font-bold mb-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>Aprobadas</span>
          </div>
          <div className="text-lg font-extrabold text-emerald-950">{formatHorasDecimales(horasAprobadas)}</div>
          <div className="text-[10px] text-emerald-700 font-semibold">{pctAprobadas}% del total meta</div>
        </div>

        {/* Rechazadas */}
        <div className="bg-rose-50/70 border border-rose-200/80 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-rose-800 text-[11px] font-bold mb-1">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
            <span>Rechazadas</span>
          </div>
          <div className="text-lg font-extrabold text-rose-950">{formatHorasDecimales(horasRechazadas)}</div>
          <div className="text-[10px] text-rose-700 font-semibold">{pctRechazadas}% en corrección</div>
        </div>

        {/* Aprobadas en corrección: audios ya aprobados dentro de una asesoría rechazada */}
        <div className="bg-amber-50/70 border border-amber-200/80 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-amber-800 text-[11px] font-bold mb-1">
            <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
            <span>Aprobadas en Corrección</span>
          </div>
          <div className="text-lg font-extrabold text-amber-950">{formatHorasDecimales(horasAprobadasEnRechazadas)}</div>
          <div className="text-[10px] text-amber-700 font-semibold">de audios ya aprobados</div>
        </div>

        {/* Pendientes */}
        <div className="bg-blue-50/70 border border-blue-200/80 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-blue-800 text-[11px] font-bold mb-1">
            <Hourglass className="w-3.5 h-3.5 text-blue-600" />
            <span>En Cola / Curso</span>
          </div>
          <div className="text-lg font-extrabold text-blue-950">{formatHorasDecimales(horasPendientes)}</div>
          <div className="text-[10px] text-blue-700 font-semibold">{pctPendientes}% en revisión</div>
        </div>

        {/* Restantes */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="flex items-center gap-1.5 text-gray-700 text-[11px] font-bold mb-1">
            <Calendar className="w-3.5 h-3.5 text-gray-500" />
            <span>Por Cumplir</span>
          </div>
          <div className="text-lg font-extrabold text-gray-900">{formatHorasDecimales(horasRestantes)}</div>
          <div className="text-[10px] text-gray-500 font-semibold">Horas faltantes</div>
        </div>
      </div>
    </div>
  );
}

export function computeResumenHoras(asesorias: any[], metaHoras = 320): ResumenHorasEmpresa {
  let horasTotales = 0;
  let horasAprobadas = 0;
  let horasRechazadas = 0;
  let horasAprobadasEnRechazadas = 0;
  let horasPendientes = 0;

  for (const a of asesorias) {
    const h = Number(a.horas) || horasDeAudios(a.audios);
    horasTotales += h;

    // No existe un estado 'RECHAZADA' en AsesoriaEstado — lo más parecido es 'REQUIERE_CORRECCIONES',
    // que ya se chequea aparte más abajo (mismo criterio que excel.service.ts en el backend).
    const tieneRechazos =
      a.evaluaciones?.some((e: any) => e.estadoRevision === 'RECHAZADO') ||
      a.evidencias?.some((e: any) => e.estado === 'RECHAZADO') ||
      a.audios?.some((e: any) => e.estado === 'RECHAZADO');

    // FINALIZADA (aprobación real) y EN_REVISION con autoAprobar (preaprobación interna) cuentan
    // AMBAS como "aprobadas" de cara al admin — no se distinguen visualmente en ningún lado.
    if (a.estado === 'FINALIZADA' || ((a.estado === 'EN_REVISION' || a.estado === 'SIN_EVALUAR') && a.autoAprobar && !tieneRechazos)) {
      horasAprobadas += h;
    } else {
      // Las horas de audios ya aprobados individualmente SON horas aprobadas, aunque la asesoría
      // en general siga rechazada o pendiente: solo el resto va a su balde. Mismo criterio que
      // `repartirHoras` en el backend (shared/horas-reporte-asesor.ts) — antes esta vista dejaba
      // el total en rechazadas y mostraba lo aprobado solo como dato informativo, así que la
      // vista y el reporte daban números distintos sobre los mismos datos.
      const aprobadoParcial = horasDeAudiosAprobados(a.audios);
      horasAprobadas += aprobadoParcial;
      const resto = h - aprobadoParcial;

      if (a.estado === 'REQUIERE_CORRECCIONES' || tieneRechazos) {
        horasRechazadas += resto;
        // Se conserva para el tile "Aprobadas en Corrección", que muestra cuánto de lo rechazado
        // ya venía aprobado a nivel de audio.
        horasAprobadasEnRechazadas += aprobadoParcial;
      } else {
        horasPendientes += resto;
      }
    }
  }

  return {
    horasTotales,
    horasAprobadas,
    horasRechazadas,
    horasAprobadasEnRechazadas,
    horasPendientes,
    horasRestantes: Math.max(0, metaHoras - horasAprobadas),
    pctAprobadas: Math.round((horasAprobadas / metaHoras) * 1000) / 10,
    pctRechazadas: Math.round((horasRechazadas / metaHoras) * 1000) / 10,
    pctPendientes: Math.round((horasPendientes / metaHoras) * 1000) / 10,
    metaHoras,
  };
}
